import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLoaderData } from 'react-router-dom';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CFormLabel, CFormInput, CFormTextarea, CBadge, CFormSelect, CAlert,
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
} from '@coreui/react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import AxiosInstance from 'src/components/Axios';
import { useResponderTeams } from 'src/hooks/useResponderTeams';
import { useSupportCategories } from 'src/hooks/useSupportCategories';
import { useSupportChannels } from 'src/hooks/useSupportChannels';
import { STATUS_COLORS, STATUS_LABELS, STATUS_LETTER_OPTIONS, formatDateTime } from 'src/constants/supportLetters';
import {
	setCachedRequest, invalidateCachedRequest, invalidateLettersList,
	patchCachedSession, patchCachedSupportHistory, invalidateCachedSession, invalidateCachedSupportHistory,
} from 'src/utils/supportRequestCache';
import { isValidEmailInput, formatPhoneInput, isValidPhoneInput, phoneForApi, PHONE_PREFIX } from 'src/utils/inputValidation';
import { isValidEmailFormat } from 'src/utils/email';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { MediaViewerModal, MediaThumbGallery } from 'src/components/myComponents/MediaViewerModal';
import SupportLetterReplyModal from './SupportLetterReplyModal';
import SupportChangeSessionModal from './SupportChangeSessionModal';
import SupportLetterAttachmentUploadModal from './SupportLetterAttachmentUploadModal';
import SupportAttachmentImportNotice from './SupportAttachmentImportNotice';

const urlApiRequests = '/support-requests';
const urlApiSessions = '/support-sessions';
const urlApiletters = '/support-letters';

export async function LoaderSupportLettersForm({ params }) {
	return AxiosInstance.get(`${urlApiRequests}/${params.id}`);
}

// "YYYY-MM-DD HH:mm:ss" (формат бекенду) → "YYYY-MM-DDTHH:mm" (формат datetime-local)
const toDatetimeLocal = (val) => val ? val.replace(' ', 'T').slice(0, 16) : '';

const SupportLettersForm = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const loaderData = useLoaderData();

	const allTeams = useResponderTeams();
	const allCategories = useSupportCategories();
	const channels = useSupportChannels();

	const [data, setData] = useState(loaderData?.data ?? null);

	// Основні поля запиту (email, ім'я, телефон, канал, дата) — тема НЕ редагується, наслідує тему сесії
	const [infoEmail, setInfoEmail] = useState('');
	const [infoName, setInfoName] = useState('');
	const [infoPhone, setInfoPhone] = useState(PHONE_PREFIX);
	const [infoChannelId, setInfoChannelId] = useState('');
	const [infoSentAt, setInfoSentAt] = useState('');
	const [infoMessage, setInfoMessage] = useState('');
	const [initialInfoEmail, setInitialInfoEmail] = useState('');
	const [initialInfoName, setInitialInfoName] = useState('');
	const [initialInfoPhone, setInitialInfoPhone] = useState(PHONE_PREFIX);
	const [initialInfoChannelId, setInitialInfoChannelId] = useState('');
	const [initialInfoSentAt, setInitialInfoSentAt] = useState('');
	const [initialInfoMessage, setInitialInfoMessage] = useState('');
	const [infoErrors, setInfoErrors] = useState({});
	const [infoSaveLoading, setInfoSaveLoading] = useState(false);

	// Статус запиту
	const [selectedStatus, setSelectedStatus] = useState('');
	const [initialStatus, setInitialStatus] = useState('');

	// Команди відповідальних
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [initialTeams, setInitialTeams] = useState([]);

	// Категорії сесії
	const [sessionCategories, setSessionCategories] = useState([]);
	const [initialCategories, setInitialCategories] = useState([]);
	const [sessionLoaded, setSessionLoaded] = useState(false);

	// Зберігання змін
	const [saveLoading, setSaveLoading] = useState(false);

	// Захист від повторної ініціалізації форми при зміні data (наприклад після handleSave/handleMarkReplied)
	const formInitializedForId = useRef(null);

	// Чернетка
	const [currentDraft, setCurrentDraft] = useState(null);
	const [draftText, setDraftText] = useState('');
	const [finalText, setFinalText] = useState('');
	const [draftLoading, setDraftLoading] = useState(false);

	// Modal: зберегти в Q&A
	const [qaModalOpen, setQaModalOpen] = useState(false);
	const [qaCategories, setQaCategories] = useState([]);
	const [qaLoading, setQaLoading] = useState(false);

	// Modal: змінити сесію
	const [sessionModalOpen, setSessionModalOpen] = useState(false);

	// Вкладення листа
	const [attachmentUploadOpen, setAttachmentUploadOpen] = useState(false);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);
	const [attachmentActionLoading, setAttachmentActionLoading] = useState(null);

	// Modal: відповісти на лист
	const [sessionRequests, setSessionRequests] = useState([]);
	const [sessionClientData, setSessionClientData] = useState({ user_email: '', user_name: '', user_phone: '' });
	const [replyModalOpen, setReplyModalOpen] = useState(false);

	const teamOptions = allTeams.map(t => ({ value: t.id, label: `${t.name} (${t.code})` }));

	const catOptions = allCategories
		.filter(c => c.scopes?.some(s => s === 'conversation' || s === 'all'))
		.map(c => ({ value: c.id, label: c.name }));

	const qaOptions = allCategories
		.filter(c => c.scopes?.some(s => s === 'knowledge_base' || s === 'all'))
		.map(c => ({ value: c.id, label: c.name }));

	// Ініціалізація стану з даних лоадера
	useEffect(() => {
		if (!data) return;
		setCachedRequest(id, data);
		// Ініціалізуємо поля форми лише один раз на id —
		// щоб повторні setData (після save/markReplied) не скидали вибрані значення
		if (formInitializedForId.current !== id) {
			formInitializedForId.current = id;
			const mapped = (data.responders || []).map(r => ({ value: r.team_id, label: `${r.team_name} (${r.team_code})` }));
			setSelectedTeams(mapped);
			setInitialTeams(mapped);
			setSelectedStatus(data.status ?? '');
			setInitialStatus(data.status ?? '');

			const email = data.user_email || '';
			const name = data.user_name || '';
			const phone = data.user_phone ? formatPhoneInput(data.user_phone) ?? PHONE_PREFIX : PHONE_PREFIX;
			const channelId = data.channel?.id ? String(data.channel.id) : '';
			const sentAt = toDatetimeLocal(data.sent_at);
			const message = data.message || '';
			setInfoEmail(email);
			setInfoName(name);
			setInfoPhone(phone);
			setInfoChannelId(channelId);
			setInfoSentAt(sentAt);
			setInfoMessage(message);
			setInitialInfoEmail(email);
			setInitialInfoName(name);
			setInitialInfoPhone(phone);
			setInitialInfoChannelId(channelId);
			setInitialInfoSentAt(sentAt);
			setInitialInfoMessage(message);
		}
		const latestDraft = (data.drafts || []).slice(-1)[0] || null;
		setCurrentDraft(latestDraft);
		setDraftText(latestDraft?.draft_text || '');
		setFinalText(latestDraft?.final_text || '');
	}, [data, id]);

	// Завантаження категорій сесії
	useEffect(() => {
		if (!data?.session_id || sessionLoaded) return;
		setSessionLoaded(true);
		AxiosInstance.get(`${urlApiSessions}/${data.session_id}`)
			.then(res => {
				const mapped = (res.data.categories || []).map(c => ({ value: c.id, label: c.name }));
				setSessionCategories(mapped);
				setInitialCategories(mapped);
				setSessionRequests(res.data.requests || []);
				setSessionClientData({
					user_email: res.data.user_email || '',
					user_name: res.data.user_name || '',
					user_phone: res.data.user_phone || '',
				});
			})
			.catch(() => { });
	}, [data, sessionLoaded]);

	// ── Редагування основних полів запиту ─────────────────────────────────────

	const hasInfoChanges = infoEmail !== initialInfoEmail
		|| infoName !== initialInfoName
		|| infoPhone !== initialInfoPhone
		|| infoChannelId !== initialInfoChannelId
		|| infoSentAt !== initialInfoSentAt
		|| infoMessage !== initialInfoMessage;

	const handleSaveInfo = async () => {
		const newErrors = {
			email: infoEmail.trim() !== '' && !isValidEmailFormat(infoEmail),
			name: !infoName.trim(),
			phone: !isValidPhoneInput(infoPhone),
			channelId: !infoChannelId,
			sentAt: !infoSentAt,
			message: !infoMessage.trim(),
		};
		if (Object.values(newErrors).some(Boolean)) {
			setInfoErrors(newErrors);
			const missing = [
				newErrors.email && 'Email (невірний формат)',
				newErrors.name && "Ім'я",
				newErrors.phone && 'Телефон (невірний формат)',
				newErrors.channelId && 'Канал',
				newErrors.sentAt && 'Дата отримання',
				newErrors.message && 'Повідомлення',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setInfoErrors({});
		setInfoSaveLoading(true);
		const oldEmail = data.user_email;
		try {
			const res = await AxiosInstance.put(`${urlApiRequests}/${id}`, {
				user_email: infoEmail.trim(),
				user_name: infoName.trim(),
				user_phone: phoneForApi(infoPhone),
				channel_id: parseInt(infoChannelId),
				sent_at: infoSentAt,
				message: infoMessage.trim(),
			});
			setData(res.data);
			setInitialInfoEmail(infoEmail);
			setInitialInfoName(infoName);
			setInitialInfoPhone(infoPhone);
			setInitialInfoChannelId(infoChannelId);
			setInitialInfoSentAt(infoSentAt);
			setInitialInfoMessage(infoMessage);
			invalidateLettersList();
			invalidateCachedRequest(id);
			if (res.data.session_id) invalidateCachedSession(res.data.session_id);
			if (oldEmail) invalidateCachedSupportHistory(oldEmail);
			if (res.data.user_email && res.data.user_email !== oldEmail) invalidateCachedSupportHistory(res.data.user_email);
			toast.success('Збережено');
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження');
		} finally {
			setInfoSaveLoading(false);
		}
	};

	const handleCancelInfo = () => {
		setInfoEmail(initialInfoEmail);
		setInfoName(initialInfoName);
		setInfoPhone(initialInfoPhone);
		setInfoChannelId(initialInfoChannelId);
		setInfoSentAt(initialInfoSentAt);
		setInfoMessage(initialInfoMessage);
		setInfoErrors({});
	};

	// ── Вкладення листа ────────────────────────────────────────────────────────

	const handleDeleteAttachment = (mediaId, attachmentName) => {
		confirmAlert({
			title: `Видалити вкладення "${attachmentName || ''}"?`,
			message: 'Дію не можна скасувати.',
			buttons: [
				{
					label: 'Так',
					onClick: async () => {
						setAttachmentActionLoading(mediaId);
						try {
							const res = await AxiosInstance.delete(`${urlApiRequests}/${id}/attachments/${mediaId}`);
							setData(res.data);
							invalidateCachedRequest(id);
							invalidateLettersList();
							if (res.data.session_id) invalidateCachedSession(res.data.session_id);
							if (res.data.user_email) invalidateCachedSupportHistory(res.data.user_email);
							toast.success('Вкладення видалено');
						} catch (err) {
							toast.error(err.response?.data?.error || 'Помилка видалення вкладення');
						} finally {
							setAttachmentActionLoading(null);
						}
					},
				},
				{ label: 'Ні', onClick: () => { } },
			],
		});
	};

	// ── Відкладені зміни ───────────────────────────────────────────────────────

	const statusChanged = selectedStatus !== initialStatus;
	const teamsChanged = selectedTeams.map(t => t.value).sort().join(',')
		!== initialTeams.map(t => t.value).sort().join(',');
	const categoriesChanged = sessionCategories.map(c => c.value).sort().join(',')
		!== initialCategories.map(c => c.value).sort().join(',');
	const hasChanges = statusChanged || teamsChanged || categoriesChanged;

	const handleSave = async () => {
		const doStatus = statusChanged;
		const doTeams = teamsChanged;
		const doCategories = categoriesChanged;
		const errors = [];
		setSaveLoading(true);

		if (doStatus) {
			try {
				await AxiosInstance.patch(`${urlApiRequests}/${id}/status`, { status: selectedStatus });
				setData(prev => ({ ...prev, status: selectedStatus }));
				setInitialStatus(selectedStatus);
				invalidateLettersList();
				const numericId = parseInt(id);
				if (data?.session_id) {
					patchCachedSession(data.session_id, s => ({
						...s,
						requests: (s.requests || []).map(r => r.id === numericId ? { ...r, status: selectedStatus } : r),
					}));
				}
				if (data?.user_email) {
					patchCachedSupportHistory(data.user_email, cached => ({
						...cached,
						data: (cached.data || []).map(r => r.id === numericId ? { ...r, status: selectedStatus } : r),
					}));
				}
			} catch {
				errors.push('статус');
			}
		}

		if (doTeams) {
			try {
				const added = selectedTeams.filter(n => !initialTeams.some(p => p.value === n.value));
				const removed = initialTeams.filter(p => !selectedTeams.some(n => n.value === p.value));
				for (const t of added)
					await AxiosInstance.post(`${urlApiRequests}/${id}/responders`, { team_id: t.value });
				for (const t of removed)
					await AxiosInstance.delete(`${urlApiRequests}/${id}/responders/${t.value}`);
				setInitialTeams(selectedTeams);
				if (data?.session_id) invalidateCachedSession(data.session_id);
			} catch {
				errors.push('команди відповідальних');
			}
		}

		if (doCategories && data?.session_id) {
			try {
				const added = sessionCategories.filter(n => !initialCategories.some(p => p.value === n.value));
				const removed = initialCategories.filter(p => !sessionCategories.some(n => n.value === p.value));
				for (const c of added)
					await AxiosInstance.post(`${urlApiSessions}/${data.session_id}/support-categories`, { category_id: c.value });
				for (const c of removed)
					await AxiosInstance.delete(`${urlApiSessions}/${data.session_id}/support-categories/${c.value}`);
				setInitialCategories(sessionCategories);
				patchCachedSession(data.session_id, s => ({
					...s,
					categories: sessionCategories.map(c => ({ id: c.value, name: c.label })),
				}));
			} catch {
				errors.push('категорії сесії');
			}
		}

		invalidateCachedRequest(id);

		if (errors.length === 0) {
			toast.success('Збережено');
		} else {
			toast.error(`Помилка збереження: ${errors.join(', ')}. Решта змін збережено.`);
		}
		setSaveLoading(false);
	};

	const handleCancel = () => {
		setSelectedStatus(initialStatus);
		setSelectedTeams(initialTeams);
		setSessionCategories(initialCategories);
	};

	// ── Чернетка ────────────────────────────────────────────────────────────────

	const handleSaveDraft = async () => {
		if (!draftText.trim()) { toast.warning('Введіть текст чернетки'); return; }
		setDraftLoading(true);
		try {
			let res;
			if (currentDraft?.id) {
				res = await AxiosInstance.put(`/response-drafts/${currentDraft.id}`, { draft_text: draftText });
			} else {
				res = await AxiosInstance.post(`${urlApiRequests}/${id}/drafts`, { draft_text: draftText });
			}
			setCurrentDraft(res.data);
			setDraftText(res.data.draft_text || '');
			invalidateCachedRequest(id);
			toast.success('Чернетку збережено');
		} catch {
			toast.error('Помилка збереження чернетки');
		} finally {
			setDraftLoading(false);
		}
	};

	const handleApproveDraft = async () => {
		if (!currentDraft?.id) { toast.warning('Спочатку збережіть чернетку'); return; }
		setDraftLoading(true);
		try {
			const res = await AxiosInstance.post(`/response-drafts/${currentDraft.id}/approve`);
			setCurrentDraft(res.data);
			setFinalText(res.data.final_text || finalText);
			invalidateCachedRequest(id);
			toast.success('Чернетку погоджено');
		} catch {
			toast.error('Помилка погодження');
		} finally {
			setDraftLoading(false);
		}
	};

	const handleSaveFinal = async () => {
		if (!currentDraft?.id) return;
		setDraftLoading(true);
		try {
			const res = await AxiosInstance.put(`/response-drafts/${currentDraft.id}`, { final_text: finalText });
			setCurrentDraft(res.data);
			invalidateCachedRequest(id);
			toast.success('Фінальну відповідь збережено');
		} catch {
			toast.error('Помилка збереження');
		} finally {
			setDraftLoading(false);
		}
	};

	const handleSaveToQa = async () => {
		if (!currentDraft?.id) return;
		setQaLoading(true);
		try {
			const res = await AxiosInstance.post(`/response-drafts/${currentDraft.id}/save-to-qa`, {
				category_ids: qaCategories.map(c => c.value),
			});
			setCurrentDraft(prev => ({ ...prev, saved_to_qa: true, qa_entry_id: res.data?.qa_entry_id }));
			invalidateCachedRequest(id);
			setQaModalOpen(false);
			setQaCategories([]);
			toast.success('Збережено в Q&A');
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження в Q&A');
		} finally {
			setQaLoading(false);
		}
	};


	if (!data) {
		return (
			<CCard className="mb-4">
				<CCardBody>
					<p className="text-danger">Запит не знайдено.</p>
					<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись">
						← Назад
					</CButton>
				</CCardBody>
			</CCard>
		);
	}

	const buttonsBlock = (
		<div className="d-flex gap-2 mb-4">
			<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись до попередньої сторінки">
				← Назад
			</CButton>
			<CButton color="info" variant="outline" onClick={() => navigate(`${urlApiletters}/view/${id}`)} title="До сторінки перегляду">
				До перегляду
			</CButton>
			{data.session_id && (
				<CButton
					color="info"
					variant="outline"
					onClick={() => navigate(`${urlApiletters}/session/${data.session_id}`)}
					title="Відкрити сторінку сесії листування"
				>
					Відкрити сесію
				</CButton>
			)}
			<CButton color="primary" onClick={() => setReplyModalOpen(true)} title="Відповісти на цей лист">
				Відповісти на лист
			</CButton>
		</div>
	);

	return (
		<>
			<ToastContainer />

			{/* ── Кнопки ── */}
			{buttonsBlock}

			{/* ── Запит — редагування ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader>
							<strong>Запит #{data.id} — редагування</strong>
						</CAccordionHeader>
						<CAccordionBody className="p-3">
							<FormSubmitOverlay submitting={infoSaveLoading}>
								<CRow className="g-3">
									<CCol md={4}>
										<CFormLabel>Email</CFormLabel>
										<CFormInput
											value={infoEmail}
											onChange={e => {
												const val = e.target.value;
												if (isValidEmailInput(val)) {
													setInfoEmail(val);
													if (infoErrors.email) setInfoErrors(p => ({ ...p, email: false }));
												}
											}}
											invalid={!!infoErrors.email}
											disabled={infoSaveLoading}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Ім'я *</CFormLabel>
										<CFormInput
											value={infoName}
											onChange={e => { setInfoName(e.target.value); if (infoErrors.name) setInfoErrors(p => ({ ...p, name: false })); }}
											invalid={!!infoErrors.name}
											disabled={infoSaveLoading}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Телефон</CFormLabel>
										<CFormInput
											value={infoPhone}
											onChange={e => {
												const formatted = formatPhoneInput(e.target.value);
												if (formatted !== null) {
													setInfoPhone(formatted);
													if (infoErrors.phone) setInfoErrors(p => ({ ...p, phone: false }));
												}
											}}
											invalid={!!infoErrors.phone}
											disabled={infoSaveLoading}
											autoComplete="off"
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Тема</CFormLabel>
										<CFormInput
											value={data.subject || ''}
											title={data.subject || ''}
											disabled readOnly
											style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Статус</CFormLabel>
										<div className="pt-2">
											<CBadge color={STATUS_COLORS[data.status] || 'secondary'} style={{ fontSize: '0.9rem' }}>
												{STATUS_LABELS[data.status] || data.status || '—'}
											</CBadge>
										</div>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Канал *</CFormLabel>
										<CFormSelect
											value={infoChannelId}
											onChange={e => { setInfoChannelId(e.target.value); if (infoErrors.channelId) setInfoErrors(p => ({ ...p, channelId: false })); }}
											invalid={!!infoErrors.channelId}
											disabled={infoSaveLoading}
										>
											<option value="">— оберіть канал —</option>
											{channels.map(ch => (
												<option key={ch.id} value={ch.id}>{ch.name}</option>
											))}
										</CFormSelect>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Дата і час отримання *</CFormLabel>
										<CFormInput
											type="datetime-local"
											value={infoSentAt}
											onChange={e => { setInfoSentAt(e.target.value); if (infoErrors.sentAt) setInfoErrors(p => ({ ...p, sentAt: false })); }}
											invalid={!!infoErrors.sentAt}
											disabled={infoSaveLoading}
										/>
									</CCol>
									<CCol md={4}>
										<CFormLabel>Дата внесення</CFormLabel>
										<CFormInput value={formatDateTime(data.created_at)} disabled readOnly />
									</CCol>
									<CCol md={4}>
										<CFormLabel>Останнє оновлення</CFormLabel>
										<CFormInput value={formatDateTime(data.updated_at)} disabled readOnly />
									</CCol>
									{data.call_center_id && (
										<CCol md={4}>
											<CFormLabel>ID в кол-центрі</CFormLabel>
											<CFormInput value={data.call_center_id} disabled readOnly />
										</CCol>
									)}
									{data.account && (
										<CCol md={4}>
											<CFormLabel>Особовий рахунок</CFormLabel>
											<CFormInput value={data.account} disabled readOnly />
										</CCol>
									)}
								</CRow>
								<div className="mt-3">
									<CFormLabel>Повідомлення *</CFormLabel>
									<CFormTextarea
										rows={4}
										value={infoMessage}
										onChange={e => { setInfoMessage(e.target.value); if (infoErrors.message) setInfoErrors(p => ({ ...p, message: false })); }}
										invalid={!!infoErrors.message}
										disabled={infoSaveLoading}
										placeholder="Текст повідомлення від клієнта..."
									/>
								</div>
								<div className="d-flex gap-2 mt-3">
									<CButton
										color="success"
										onClick={handleSaveInfo}
										disabled={!hasInfoChanges || infoSaveLoading}
										title="Зберегти зміни email, імені, телефону, каналу і дати"
									>
										{infoSaveLoading ? 'Збереження...' : 'Зберегти'}
									</CButton>
									<CButton
										color="secondary"
										variant="outline"
										onClick={handleCancelInfo}
										disabled={!hasInfoChanges || infoSaveLoading}
										title="Скасувати незбережені зміни"
									>
										Скасувати
									</CButton>
								</div>
							</FormSubmitOverlay>

							<hr className="my-4" />

							<div className="border rounded p-2">
								<FormSubmitOverlay submitting={attachmentActionLoading !== null}>
									<SupportAttachmentImportNotice
										imports={data.attachment_imports}
										requestId={parseInt(id)}
										sessionId={data?.session_id}
										userEmail={data?.user_email}
										onAcknowledged={(req) => setData(req)}
									/>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<strong>Вкладення ({(data.attachments || []).length})</strong>
										<CButton
											color="secondary"
											variant="outline"
											onClick={() => setAttachmentUploadOpen(true)}
											disabled={infoSaveLoading}
											title="Додати вкладення до листа"
										>
											+ Додати вкладення
										</CButton>
									</div>
									{(data.attachments || []).length === 0 ? (
										<div className="text-muted small">Вкладень немає</div>
									) : (
										<MediaThumbGallery
											attachments={data.attachments}
											onSelect={(idx) => { setViewerIndex(idx); setViewerOpen(true); }}
											onDelete={handleDeleteAttachment}
											disabled={infoSaveLoading}
										/>
									)}
									<div className="d-flex align-items-center gap-3 mt-2">
										<CButton
											color="secondary"
											variant="outline"
											onClick={() => { setViewerIndex(0); setViewerOpen(true); }}
											disabled={(data.attachments || []).length === 0}
											title="Переглянути вкладення"
										>
											Переглянути вкладення
										</CButton>
										<span className="text-muted small">
											Вкладення зберігаються одразу, окремо від кнопки «Зберегти» вище
										</span>
									</div>
								</FormSubmitOverlay>
							</div>
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Редагування даних запиту ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader><strong>Редагування статусу листа, команди відповідальних за лист, категорії сесії</strong></CAccordionHeader>
						<CAccordionBody className="p-3">
							<FormSubmitOverlay submitting={saveLoading}>
								<CRow className="g-3">
									{/* Статус */}
									<CCol md={4}>
										<CFormLabel>Статус листа</CFormLabel>
										<CFormSelect
											value={selectedStatus}
											onChange={e => setSelectedStatus(e.target.value)}
											disabled={saveLoading}
										>
											{STATUS_LETTER_OPTIONS.map(o => (
												<option key={o.value} value={o.value}>{o.label}</option>
											))}
										</CFormSelect>
									</CCol>

									{/* Команди відповідальних */}
									<CCol md={8}>
										<CFormLabel>Команди відповідальних</CFormLabel>
										<Select
											isMulti
											options={teamOptions}
											value={selectedTeams}
											onChange={setSelectedTeams}
											isDisabled={saveLoading}
											placeholder="Оберіть команди..."
											isClearable
											classNamePrefix="react-select"
										/>
									</CCol>

									{/* Категорії сесії — тільки якщо session_id є */}
									{data.session_id && (
										<CCol md={12}>
											<CFormLabel>Категорії сесії</CFormLabel>
											<Select
												isMulti
												options={catOptions}
												value={sessionCategories}
												onChange={setSessionCategories}
												isDisabled={saveLoading}
												placeholder="Оберіть категорії..."
												isClearable
												classNamePrefix="react-select"
											/>
										</CCol>
									)}
								</CRow>

								{/* Кнопки */}
								<div className="d-flex gap-2 mt-3">
									<CButton
										color="success"
										onClick={handleSave}
										disabled={!hasChanges || saveLoading}
										title="Зберегти зміни статусу, команд і категорій"
									>
										{saveLoading ? 'Збереження...' : 'Зберегти'}
									</CButton>
									<CButton
										color="secondary"
										variant="outline"
										onClick={handleCancel}
										disabled={!hasChanges || saveLoading}
										title="Скасувати незбережені зміни"
									>
										Скасувати
									</CButton>
								</div>
							</FormSubmitOverlay>
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Відповісти на лист ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader><strong>Відповісти на лист</strong></CAccordionHeader>
						<CAccordionBody className="p-3">
							<CButton color="primary" onClick={() => setReplyModalOpen(true)} title="Відповісти на цей лист">
								Відповісти
							</CButton>
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Чернетка відповіді ── */}
			<CCard className="mb-4">
				<CAccordion>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader>
							<strong>Чернетка відповіді</strong>
							{currentDraft?.approved && <CBadge color="success" className="ms-2">Погоджено</CBadge>}
							{currentDraft?.saved_to_qa && <CBadge color="info" className="ms-2">В Q&A</CBadge>}
						</CAccordionHeader>
						<CAccordionBody className="p-3">
							{/* Секція: чернетка */}
							<CFormLabel>Чернетка</CFormLabel>
							<CFormTextarea
								rows={5}
								value={draftText}
								onChange={e => setDraftText(e.target.value)}
								disabled={currentDraft?.approved === true}
								placeholder="Введіть текст чернетки..."
							/>
							{currentDraft?.approved && currentDraft.approved_by_name && (
								<div className="text-muted small mt-1">
									Погоджено: {currentDraft.approved_by_name}, {formatDateTime(currentDraft.approved_at)}
								</div>
							)}
							<div className="d-flex gap-2 mt-2 mb-4">
								<CButton
									color="secondary"
									onClick={handleSaveDraft}
									disabled={draftLoading || currentDraft?.approved}
									title="Зберегти текст як чернетку"
								>
									Зберегти чернетку
								</CButton>
								<CButton
									color="success"
									onClick={handleApproveDraft}
									disabled={draftLoading || !currentDraft?.id || currentDraft?.approved}
									title="Погодити чернетку"
								>
									Погодити ✓
								</CButton>
							</div>

							{/* Секція: фінальна відповідь */}
							<CFormLabel>Фінальна відповідь</CFormLabel>
							{currentDraft?.saved_to_qa && (
								<CAlert color="warning" className="py-2 mb-2">
									⚠️ Текст вже збережено в Q&A. Зміни тут не оновлять Q&A автоматично.
									{currentDraft.qa_entry_id && (
										<> <Link to={`/qa/view/${currentDraft.qa_entry_id}`}>→ Переглянути запис Q&A #{currentDraft.qa_entry_id}</Link></>
									)}
								</CAlert>
							)}
							<CFormTextarea
								rows={5}
								value={finalText}
								onChange={e => setFinalText(e.target.value)}
								placeholder="Фінальна відповідь клієнту..."
							/>
							<div className="d-flex gap-2 mt-2">
								<CButton
									color="secondary"
									variant="outline"
									onClick={() => setFinalText(draftText)}
									title="Скопіювати текст чернетки у фінальну відповідь"
								>
									← Скопіювати з чернетки
								</CButton>
								<CButton
									color="primary"
									onClick={handleSaveFinal}
									disabled={draftLoading || !currentDraft?.id}
									title="Зберегти фінальну відповідь"
								>
									Зберегти фінал
								</CButton>
								<CButton
									color="info"
									onClick={() => setQaModalOpen(true)}
									disabled={!currentDraft?.id || !currentDraft?.approved || currentDraft?.saved_to_qa}
									title="Зберегти в базу знань Q&A (тільки після погодження)"
								>
									Зберегти в Q&A
								</CButton>
							</div>
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Сесія ── */}
			<CCard className="mb-4">
				<CAccordion activeItemKey={1}>
					<CAccordionItem itemKey={1}>
						<CAccordionHeader><strong>Сесія</strong></CAccordionHeader>
						<CAccordionBody className="p-3">
							{data.session_id
								? <div className="mb-2">Поточна сесія: <strong>#{data.session_id}</strong>{data.subject ? <> «{data.subject}»</> : ''}</div>
								: <div className="text-muted mb-2">Сесія не прив'язана</div>
							}
							<div className="d-flex align-items-center gap-3">
								<CButton
									color="secondary"
									variant="outline"
									onClick={() => setSessionModalOpen(true)}
									disabled={data.sender_type === 'admin'}
									title={data.sender_type === 'admin' ? 'Сесію можна змінювати тільки для листів від користувачів' : "Змінити або прив'язати сесію"}
								>
									Змінити сесію
								</CButton>
								{data.sender_type === 'admin' && (
									<span className="text-muted small">
										Сесію можна змінювати тільки для листів від користувачів, оскільки лист від адміна прив'язаний до листа від користувача.
									</span>
								)}
							</div>
						</CAccordionBody>
					</CAccordionItem>
				</CAccordion>
			</CCard>

			{/* ── Кнопки ── */}
			{buttonsBlock}

			{/* ── Modal: Зберегти в Q&A ── */}
			<CModal visible={qaModalOpen} onClose={() => { setQaModalOpen(false); setQaCategories([]); }}>
				<CModalHeader>
					<CModalTitle>Зберегти в Q&A</CModalTitle>
				</CModalHeader>
				<CModalBody>
					<CFormLabel>Категорії Q&A</CFormLabel>
					<Select
						isMulti
						options={qaOptions}
						value={qaCategories}
						onChange={setQaCategories}
						placeholder="Оберіть категорії..."
						isClearable
						classNamePrefix="react-select"
					/>
				</CModalBody>
				<CModalFooter>
					<CButton color="primary" onClick={handleSaveToQa} disabled={qaLoading} title="Зберегти фінальну відповідь в Q&A">
						{qaLoading ? 'Збереження...' : 'Зберегти'}
					</CButton>
					<CButton color="secondary" variant="outline" onClick={() => { setQaModalOpen(false); setQaCategories([]); }} title="Скасувати">
						Скасувати
					</CButton>
				</CModalFooter>
			</CModal>

			<SupportChangeSessionModal
				open={sessionModalOpen}
				onClose={() => setSessionModalOpen(false)}
				requestId={parseInt(id)}
				currentSessionId={data?.session_id}
				userEmail={data?.user_email}
				onSuccess={({ newSessionId }) => {
					setData(prev => ({ ...prev, session_id: newSessionId ?? prev.session_id }));
				}}
			/>
			<SupportLetterAttachmentUploadModal
				open={attachmentUploadOpen}
				onClose={() => setAttachmentUploadOpen(false)}
				requestId={parseInt(id)}
				sessionId={data?.session_id}
				userEmail={data?.user_email}
				onSuccess={(updatedRequest) => setData(updatedRequest)}
			/>
			<MediaViewerModal
				open={viewerOpen}
				onClose={() => setViewerOpen(false)}
				attachments={data?.attachments || []}
				startIndex={viewerIndex}
			/>
			<SupportLetterReplyModal
				open={replyModalOpen}
				onClose={() => setReplyModalOpen(false)}
				parentRequest={data}
				sessionId={data?.session_id}
				subject={data?.subject}
				userEmail={data?.user_email}
				sessionRequests={sessionRequests}
				onSuccess={newRequest => setSessionRequests(prev => [...prev, newRequest])}
				onStatusApplied={(requestId, newStatus) => {
					if (requestId === parseInt(id)) {
						setData(prev => ({ ...prev, status: newStatus }));
						setSelectedStatus(newStatus);
						setInitialStatus(newStatus);
					}
				}}
				clientData={data?.sender_type !== 'admin'
					? { user_email: data?.user_email, user_name: data?.user_name, user_phone: data?.user_phone }
					: sessionClientData
				}
				adminData={JSON.parse(localStorage.getItem('user_data') || '{}')}
			/>
		</>
	);
};

export default SupportLettersForm;
